import React from 'react';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetClose,
} from './ui/sheet';
import { Button } from './ui/button';
import { HelpCircle, Wand2, MessageCircle, Lightbulb, ExternalLink, X } from 'lucide-react';

// Bite-sized, in-app version of the /docs "Using guided steps" section.
// Deep-links to the matching anchor on the full help center.
const STEP_RHYTHM = [
  { n: 1, title: 'Read your mission', body: 'The "Your Mission" card at the top tells you exactly what you\'re creating and why it matters.' },
  { n: 2, title: 'Fill the box & press Guide Me', body: 'Type a sentence or two in plain words. Creataby writes the copy, outline, or script for you.' },
  { n: 3, title: 'Review, tweak & save', body: 'Like it? Press "Save All to Workspace". Want a fresh angle? Press "Redo". Edit anything you want.' },
  { n: 4, title: 'Mark complete', body: 'Lock in your progress and preview the next step. Your project builds itself, one step at a time.' },
];

const QUICK_TIPS = [
  'Be specific — real details produce sharper results.',
  'Write like you talk. Creataby handles the polish.',
  'Save a "good enough" version now; refine it later.',
];

const DOCS_URL = '/docs#guided-steps';

export const HelpPanel = ({ triggerClassName = '' }) => {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={`h-8 w-8 text-muted-foreground hover:text-teal-600 ${triggerClassName}`}
          aria-label="Help with this step"
          data-testid="step-help-button"
        >
          <HelpCircle className="h-4 w-4" />
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md overflow-y-auto p-0" data-testid="step-help-panel">
        <SheetHeader className="px-6 pt-6 pb-4 border-b border-border/60 sticky top-0 bg-background/95 backdrop-blur-sm z-10">
          <div className="flex items-center justify-between">
            <SheetTitle className="flex items-center gap-2 text-base">
              <span className="w-8 h-8 rounded-lg bg-teal-50 flex items-center justify-center">
                <Wand2 className="h-4 w-4 text-teal-600" />
              </span>
              How each step works
            </SheetTitle>
            <SheetClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Close help" data-testid="step-help-close">
                <X className="h-4 w-4" />
              </Button>
            </SheetClose>
          </div>
        </SheetHeader>

        <div className="px-6 py-5 space-y-6">
          <p className="text-sm text-muted-foreground leading-relaxed">
            Every step follows the same simple rhythm. Here's what to do on this screen:
          </p>

          {/* Step rhythm */}
          <div className="space-y-4">
            {STEP_RHYTHM.map((s) => (
              <div key={s.n} className="flex gap-3" data-testid={`help-rhythm-${s.n}`}>
                <div className="flex-shrink-0 w-7 h-7 rounded-full bg-teal-600 text-white text-xs font-bold flex items-center justify-center shadow-sm">
                  {s.n}
                </div>
                <div className="pt-0.5">
                  <p className="font-semibold text-sm text-foreground">{s.title}</p>
                  <p className="text-sm text-muted-foreground leading-relaxed mt-0.5">{s.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Reggie tip */}
          <div className="rounded-xl border border-teal-100 bg-teal-50/50 p-4">
            <p className="font-semibold text-sm text-foreground flex items-center gap-2 mb-1">
              <MessageCircle className="h-4 w-4 text-teal-600" />
              Stuck? Ask Reggie
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Tap the "Ask Reggie" button anytime for a plain-English explanation or a suggested answer you can drop straight into the box.
            </p>
          </div>

          {/* Quick tips */}
          <div>
            <p className="font-semibold text-sm text-foreground flex items-center gap-2 mb-2">
              <Lightbulb className="h-4 w-4 text-amber-500" />
              Tips for best results
            </p>
            <ul className="space-y-2">
              {QUICK_TIPS.map((t) => (
                <li key={t} className="text-sm text-muted-foreground leading-relaxed flex gap-2">
                  <span className="text-teal-600 mt-1">•</span>
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Full docs link */}
          <a href={DOCS_URL} target="_blank" rel="noopener noreferrer" data-testid="help-open-docs">
            <Button variant="outline" className="w-full border-border">
              Read the full guides
              <ExternalLink className="h-4 w-4 ml-2" />
            </Button>
          </a>
        </div>
      </SheetContent>
    </Sheet>
  );
};
